import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider, Modal, theme as antdTheme } from "antd";
import UnitPlanningCalculator from "../../page/unitPlanning/UnitPlanningCalculator";
import { rememberPublicCalculation } from "../../page/unitPlanning/pendingPublicCalc";
import { METRIKA_GOALS, trackMetrikaGoal } from "../../utilt/bootstrapAlert/yandexMetrica";
import { getEntrySearch } from "../referral/attributionStorage";
import { readEntry } from "./entrySource";
import { CardsWidget, MoneyWidget, RecsWidget, ReviewsWidget, StockWidget } from "./landingScenarios";
import "./landing.css";

// Лендинг по сценариям (прототип v13). Первый экран показывает одну задачу селлера:
// её выбирают чипом или группа объявления из utm_content (?g=). Вход — существующая
// карточка из aouth.jsx в модальном окне.

const SCENARIOS = {
  calc: {
    chip: "Посчитать новый товар",
    h1: ["Сколько вы ", "заработаете", " на товаре?"],
    sub: "Комиссия, логистика, выкуп, реклама и налог по категории Wildberries и Ozon. Регистрация не нужна. В полях уже стоит пример: поменяйте цифры на свои.",
    ticks: ["Тарифы Wildberries и Ozon по категории и схеме", "Подбор цены под нужную прибыль или маржу", "Сохраните расчёт, когда захотите"],
  },
  recs: {
    chip: "Что сделать сегодня",
    cta: "Получить рекомендации для своего магазина",
    h1: ["Подскажем, что сделать, ", "чтобы заработать больше", ""],
    sub: "AI-рекомендации разбирают прошлую неделю по каждому товару: маржу, остатки, выкуп и возвраты. Вы видите, что сделать сегодня, что на этой неделе и за чем просто наблюдать.",
    ticks: ["Решение по каждому товару, а не просто графики", "Почему именно так: объяснение на ваших цифрах", "Задача из рекомендации в один клик"],
  },
  money: {
    chip: "Сколько я зарабатываю",
    cta: "Посчитать мой магазин",
    h1: ["Продажи есть, а денег нет? Покажем, ", "куда они уходят", ""],
    sub: "Юнит-экономика каждого товара по реальным продажам и P&L магазина по месяцам: комиссии, логистика, хранение, реклама, себестоимость и налоги. Данные приходят из отчётов маркетплейса сами.",
    ticks: ["Прибыль по каждому товару, а не «в среднем по больнице»", "P&L по месяцам с налогами и сравнением с прошлым месяцем", "Себестоимость одним файлом Excel, выгрузка отчётов в Excel"],
  },
  reviews: {
    chip: "Ответы на отзывы",
    cta: "Подключить отзывы",
    h1: ["Ответы на отзывы ", "по вашим правилам", ""],
    sub: "Ответ на отзыв Wildberries или Ozon готовится в один клик: вы проверяете, правите и публикуете. Для типовых отзывов можно настроить правила и отправку готовым шаблоном.",
    ticks: ["Обращение, длина и стиль ответа настраиваете сами", "Шаблоны и правила для типовых отзывов", "Wildberries и Ozon в одном разделе"],
  },
  stock: {
    chip: "Остатки и поставки",
    cta: "Проверить мои остатки",
    h1: ["Узнайте, какой товар ", "скоро закончится", ""],
    sub: "Упрости смотрит на остатки и заказы и считает, на сколько дней хватит товара. Файл поставки для Wildberries и Ozon можно выгрузить в Excel и сразу отдать сотруднику задачей.",
    ticks: ["Сколько дней хватит остатка", "Сколько штук и в какой кластер везти", "Готовый файл поставки и задача исполнителю"],
  },
  cards: {
    chip: "Карточки товаров",
    cta: "Заполнить свою карточку",
    h1: ["Карточка товара ", "за минуты", ", а не за вечер"],
    sub: "Напишите, что за товар. Упрости заполнит название, описание и характеристики под категорию Wildberries или Ozon и выгрузит карточку в кабинет.",
    ticks: ["Характеристики под требования категории", "Описание без воды", "Выгрузка в кабинет прямо из сервиса"],
  },
};
const LANDING_FONT = '"Onest", "Segoe UI", system-ui, sans-serif';
const ORDER = ["recs", "money", "calc", "stock", "reviews", "cards"];

// Группа объявления Директа (utm_content или ?g=, см. entrySource.js) выбирает сценарий и заголовок.
const SOURCES = {
  A1: { s: "calc", mp: "ozon", h1: ["Сколько вы ", "заработаете", " на товаре в Ozon?"] },
  A2: { s: "calc", mp: "wb", h1: ["Сколько вы ", "заработаете", " на товаре в Wildberries?"] },
  A3: { s: "calc", mp: "ozon", h1: ["Юнит-экономика товара в Ozon ", "за минуту", ""] },
  A4: { s: "calc", mp: "wb", h1: ["Юнит-экономика товара в Wildberries ", "за минуту", ""] },
  A5: { s: "calc", h1: ["Юнит-экономика товара ", "за минуту", ""] },
  A6: { s: "calc", h1: ["Прибыль с товара ", "без Excel-таблиц", ""] },
  A7: { s: "money" },
  A8: { s: "money" },
  A9: { s: "money" },
  B1: { s: "cards" },
  B2: { s: "cards" },
  B3: { s: "cards" },
  B4: { s: "reviews" },
  B5: { s: "stock" },
};

// Пример в калькуляторе: худи 3 990 ₽. На тарифах октября 2026 он даёт прибыль на обеих
// площадках (Ozon около 460 ₽, WB около 670 ₽); при выкупе 35% и цене 2 490 ₽ был убыток.
// Категория ищется в справочнике тарифа по названию, кластеры Ozon — по началу названия
// (одинаковый кластер хранения и доставки).
const CALC_EXAMPLE = {
  price: 3990,
  costPrice: 800,
  buyoutRate: 50,
  volumeLiters: 3,
  weightKg: 0.6,
  acquiringRate: 1.5,
  advertisingRate: 5,
  taxSystem: "usn_income",
  usnRate: 6,
};
const CALC_INITIAL_VALUES = {
  ozon: { ...CALC_EXAMPLE, scheme: "fbo", storageClusterId: "Москва", deliveryClusterId: "Москва" },
  wb: { ...CALC_EXAMPLE, scheme: "fbw", storageDays: 30 },
};
const CALC_CATEGORY_SEARCH = "Худи";

const JOB_STAGES = [
  {
    title: "Запустить товар",
    jobs: [
      { s: "calc", b: "Посчитать прибыль до закупки", t: "Калькулятор юнит-экономики WB и Ozon" },
      { s: "cards", b: "Быстро завести карточку", t: "Текст и характеристики под категорию, публикация в кабинет" },
    ],
  },
  {
    title: "Продавать каждый день",
    jobs: [
      { s: "recs", b: "Понять, что сделать сегодня", t: "AI-рекомендации: цена и маржа, остатки, выкуп и возвраты" },
      { s: "stock", b: "Не остаться без остатков", t: "На сколько дней хватит остатка и файл поставки в Excel" },
      { b: "Не сливать бюджет на рекламу", t: "Расход, ДРР и цена заказа по каждому товару" },
      { s: "reviews", b: "Отвечать на отзывы", t: "Ответ в один клик, правила и шаблоны" },
    ],
  },
  {
    title: "Считать деньги",
    jobs: [
      { s: "money", b: "Знать прибыль по каждому товару", t: "Юнит-экономика по реальным продажам" },
      { s: "money", b: "Закрыть месяц", t: "P&L по месяцам с налогами, выгрузка в Excel" },
      { b: "Внести себестоимость", t: "Один файл Excel на все товары" },
    ],
  },
  {
    title: "Работать командой",
    jobs: [
      { b: "Раздать работу сотрудникам", t: "Задачи из рекомендаций и поставок, повторяющиеся задачи" },
      { b: "Обсуждать без мессенджеров", t: "Чаты и видеозвонки внутри сервиса" },
      { b: "Смотреть всё с телефона", t: "Telegram-бот: юнит-экономика, поставка, себестоимость" },
    ],
  },
];
const TASK_WORDS = ["задача", "задачи", "задач"];
const pluralTasks = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return TASK_WORDS[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return TASK_WORDS[1];
  return TASK_WORDS[2];
};

const SHOT_TABS = [
  ["unit", "Юнит-экономика", "Юнит-экономика по каждому товару: продажи, цена, себестоимость, комиссия и прибыль за период. Таблицу можно выгрузить в Excel.", "Таблица юнит-экономики в Упрости"],
  ["tasks", "Задачи", "Задачи для команды: исполнитель, срок, приоритет и статус. Есть повторяющиеся задачи.", "Список задач в Упрости"],
  ["orders", "Заказы", "Заказы по неделям рядом с остатками FBS и FBO: видно, где продажи просели.", "Заказы по неделям в Упрости"],
];

const FAQ = [
  ["calc_free", "Калькулятор правда работает без регистрации?", ["Да. Считайте сколько угодно товаров. Вход по телефону нужен, только чтобы сохранить расчёт и посчитать весь магазин по реальным продажам."]],
  ["key_how", "Какой ключ нужен и где его взять?", [
    "Wildberries: в кабинете продавца откройте профиль, «Интеграции по API», «Создать токен», «Для интеграции вручную», «Персональный токен». Отметьте восемь категорий: Контент, Аналитика, Цены и скидки, Маркетплейс, Статистика, Продвижение, Финансы, Вопросы и отзывы. Уровень доступа «Чтение и запись».",
    "Ozon: ключ Seller API с типом «Admin read only» и Client ID. Для статистики рекламы можно отдельно добавить ключ Performance. Отзывы Ozon подключаются отдельно, в разделе отзывов.",
  ]],
  ["key_write", "Зачем ключу Wildberries «Чтение и запись»? Сервис что-то поменяет в кабинете?", ["Запись нужна, чтобы публиковать ответы на отзывы и карточки, которые вы создали в сервисе. Уровень доступа у ключа Wildberries один на все категории, поэтому запись включается целиком. Цены, остатки и рекламу Упрости не меняет. Ответ на отзыв уходит, только когда вы нажали «Опубликовать» или сами включили автоответы по своим правилам. Ключ можно удалить в кабинете Wildberries в разделе «Интеграции по API», и доступ пропадёт."]],
  ["recs_when", "Когда появятся рекомендации?", ["После первой загрузки данных. Рекомендации строятся по последней полной неделе, с понедельника по воскресенье. Чтобы видеть маржу и прибыль, загрузите себестоимость."]],
  ["wb_ozon", "Можно подключить и Wildberries, и Ozon?", ["Да. В одну подписку входят один кабинет Wildberries и один кабинет Ozon."]],
  ["shops", "У меня несколько магазинов. Как быть?", ["Каждый магазин подключается отдельной компанией и оплачивается отдельно, 1 290 ₽ в месяц. В компании один кабинет Wildberries и один кабинет Ozon. Бесплатные 7 дней даются на первую компанию."]],
  ["install", "Нужно что-то устанавливать?", ["Нет. Упрости работает в браузере."]],
  ["cancel", "Что будет после пробного периода?", ["Сервис предложит оплатить подписку, 1 290 ₽ в месяц. Если не оплатить, платные разделы закроются, а калькулятор останется бесплатным. Автоплатёж можно отключить."]],
];

const money = (value) =>
  new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 }).format(Number(value || 0));

const scrollBehavior = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

function readEntrySource() {
  if (typeof window === "undefined") return { source: "", scenario: "" };
  return readEntry({
    search: window.location.search || getEntrySearch(),
    hash: window.location.hash,
    sources: SOURCES,
    scenarios: SCENARIOS,
  });
}

// Сообщение о восстановлении сервиса после переезда (05.11.2026). Скрывается само после until (по Москве).
const SERVICE_NOTICE = {
  until: "2026-11-30",
  title: "Упрости снова работает.",
  text: "Со 2 по 5 ноября сервис был недоступен из-за кибератак на хостинг Beget. Мы переехали на новый сервер и восстановили данные.",
};

function moscowDay(now = Date.now()) {
  return new Date(now + 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function ServiceNotice() {
  if (moscowDay() > SERVICE_NOTICE.until) return null;
  return (
    <div className="svc-notice" role="status">
      <div className="wrap">
        <b>{SERVICE_NOTICE.title}</b> {SERVICE_NOTICE.text}
      </div>
    </div>
  );
}

function Headline({ parts }) {
  const [before, accent, after] = parts;
  return (
    <>
      {before}
      {accent ? <em>{accent}</em> : null}
      {after}
    </>
  );
}

function SaveIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15.2 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.8L15.2 3z" />
      <path d="M17 21v-7H7v7M7 3v4h7" />
    </svg>
  );
}

function Logo({ src }) {
  return (
    <a className="logo" href="/" aria-label="Упрости">
      <img src={src} alt="" width="36" height="44" />
      <span><b>Упрости</b><em>AI для селлеров</em></span>
    </a>
  );
}

export default function Landing({ isDark, logo, legal, shots, renderAuthCard, onAuthOpened }) {
  const entry = useMemo(readEntrySource, []);
  const [scenario, setScenario] = useState(() => (entry.source ? SOURCES[entry.source].s : entry.scenario || "recs"));
  const [marketplace, setMarketplace] = useState(() => SOURCES[entry.source]?.mp || "ozon");
  const [authOpen, setAuthOpen] = useState(false);
  const [authKind, setAuthKind] = useState("signin");
  const [lastCalc, setLastCalc] = useState(null);
  const [shot, setShot] = useState("unit");
  const [phone, setPhone] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(max-width: 719px)").matches : false
  );
  const [openStages, setOpenStages] = useState({});
  const heroRef = useRef(null);
  const chipsRef = useRef(null);

  useEffect(() => {
    const query = window.matchMedia?.("(max-width: 719px)");
    if (!query) return undefined;
    const sync = () => setPhone(query.matches);
    query.addEventListener?.("change", sync);
    return () => query.removeEventListener?.("change", sync);
  }, []);

  // На телефоне чипы листаются по горизонтали: выбранный сценарий (в том числе из объявления)
  // должен быть виден, а не остаться за краем экрана.
  useEffect(() => {
    const row = chipsRef.current;
    const chip = row?.querySelector('[aria-pressed="true"]');
    if (!row || !chip || row.scrollWidth <= row.clientWidth) return;
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: scrollBehavior() });
  }, [scenario, phone]);

  const current = SCENARIOS[scenario];
  const sourceHeadline = SOURCES[entry.source]?.s === scenario ? SOURCES[entry.source].h1 : null;
  const headline = sourceHeadline || current.h1;
  const headlineLong = headline.join("").length > 44;

  const selectScenario = useCallback((key, place, { scroll = false } = {}) => {
    setScenario(key);
    trackMetrikaGoal(METRIKA_GOALS.landingScenarioSelect, { scenario: key, place });
    if (scroll) heroRef.current?.scrollIntoView({ behavior: scrollBehavior() });
  }, []);

  const openAuth = useCallback((kind, place) => {
    setAuthKind(kind);
    onAuthOpened?.({ kind, place });
    setAuthOpen(true);
  }, [onAuthOpened]);

  const startTrial = useCallback((place) => {
    trackMetrikaGoal(METRIKA_GOALS.landingTrialClick, { place: place || scenario });
    openAuth("trial", place || scenario);
  }, [openAuth, scenario]);

  const startSave = useCallback((place, calc) => {
    trackMetrikaGoal(METRIKA_GOALS.calcPublicConnectClick, { marketplace, place });
    if (calc?.input) rememberPublicCalculation(marketplace, calc.input);
    openAuth("save", place);
  }, [marketplace, openAuth]);

  const handleCalculated = useCallback((calc) => {
    setLastCalc({ ...calc, marketplace });
    if (calc.auto) return;
    trackMetrikaGoal(calc.solved ? METRIKA_GOALS.calcPublicPriceSolve : METRIKA_GOALS.calcPublicDone, { marketplace });
  }, [marketplace]);

  const handleSaveRequest = useCallback((calc) => {
    if (calc?.result) setLastCalc({ ...calc, marketplace });
    startSave("calc_save", calc);
  }, [marketplace, startSave]);

  const trackCallback = (place) => trackMetrikaGoal(METRIKA_GOALS.landingCallbackClick, { place });

  const openFaq = (event, key) => {
    event.preventDefault();
    const item = document.querySelector(`.upl details[data-q="${key}"]`);
    if (!item) return;
    item.open = true;
    item.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
  };

  // Подсветка под курсором на панели примера и карточках.
  const handlePointerMove = (event) => {
    const panel = event.target.closest?.(".widget");
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    panel.style.setProperty("--mx", `${event.clientX - rect.left}px`);
    panel.style.setProperty("--my", `${event.clientY - rect.top}px`);
  };

  const savedCalc = authKind === "save" && lastCalc?.result && lastCalc.marketplace === marketplace ? lastCalc : null;

  const renderWidget = () => {
    switch (scenario) {
      case "calc":
        return (
          <>
            <div className="wlabel">
              <div className="seg" role="group" aria-label="Маркетплейс">
                <button type="button" aria-pressed={marketplace === "ozon"} onClick={() => setMarketplace("ozon")}>Ozon</button>
                <button type="button" aria-pressed={marketplace === "wb"} onClick={() => setMarketplace("wb")}>Wildberries</button>
              </div>
              <b>{marketplace === "wb" ? "WB" : "Ozon"} плановая экономика</b>
            </div>
            <div className="upl-calc">
              <UnitPlanningCalculator
                key={marketplace}
                marketplace={marketplace}
                publicMode
                isDarkOverride={isDark}
                initialValues={CALC_INITIAL_VALUES[marketplace]}
                initialCategorySearch={CALC_CATEGORY_SEARCH}
                scrollToResult
                autoCalculate
                onCalculated={handleCalculated}
                onSaveRequest={handleSaveRequest}
              />
            </div>
            <div className="calc-after">
              <button className="cta" type="button" onClick={() => startSave("calc_cta", lastCalc?.marketplace === marketplace ? lastCalc : null)}>
                Сохранить расчёт и посчитать весь магазин <span className="arr" aria-hidden="true">→</span>
              </button>
              <ul className="unlock">
                <li>Прибыль по каждому товару по реальным продажам</li>
                <li>Кластеры Ozon из вашего кабинета</li>
                <li>Сохранённые расчёты, чтобы сравнивать товары</li>
              </ul>
            </div>
          </>
        );
      case "money":
        return <MoneyWidget onTrial={startTrial} onScenario={selectScenario} />;
      case "reviews":
        return <ReviewsWidget onTrial={startTrial} />;
      case "stock":
        return <StockWidget onTrial={startTrial} />;
      case "cards":
        return <CardsWidget onTrial={startTrial} />;
      default:
        return <RecsWidget onTrial={startTrial} />;
    }
  };

  const activeShot = SHOT_TABS.find(([key]) => key === shot) || SHOT_TABS[0];
  const shotSrc = shots?.[`${activeShot[0]}${isDark ? "Dark" : ""}`];

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        // Явные цвета: ConfigProvider приложения задаёт светлые colorText и colorBgContainer,
        // и без них поля калькулятора в тёмной теме остаются тёмным текстом на тёмном фоне.
        token: isDark
          ? { fontFamily: LANDING_FONT, colorPrimary: "#4aa3ff", colorText: "#f2f0e8", colorBgContainer: "#181b30", colorBorder: "#2b2f4a", borderRadius: 10 }
          : { fontFamily: LANDING_FONT, colorPrimary: "#0a6fd6", colorText: "#14172b", colorBgContainer: "#fffdf8", colorBorder: "#e3ddcf", borderRadius: 10 },
      }}
    >
      <div className={`upl-theme upl ${isDark ? "is-dark" : ""}`} onPointerMove={handlePointerMove}>
        <ServiceNotice />
        <header className="top">
          <div className="wrap">
            <Logo src={logo} />
            <button className="signin" type="button" onClick={() => openAuth("signin", "header")}>Войти</button>
          </div>
        </header>

        <main>
          <section className="hero" ref={heroRef}>
            <div className="wrap">
              <div className="ask">
                <span id="uplAskLabel">Что хотите сделать?</span>
                <span className="ask-hint" aria-hidden="true">листайте →</span>
              </div>
              <div className="chips" role="group" aria-labelledby="uplAskLabel" ref={chipsRef}>
                {ORDER.map((key) => (
                  <button
                    key={key}
                    className="chip"
                    type="button"
                    aria-pressed={key === scenario}
                    onClick={() => selectScenario(key, "chips")}
                  >
                    {SCENARIOS[key].chip}
                  </button>
                ))}
              </div>
              <div className={`grid ${scenario === "calc" ? "is-calc" : ""}`}>
                <div className="copy">
                  <span className="kicker">Для селлеров Wildberries и Ozon</span>
                  <h1 className={headlineLong ? "long" : ""}><Headline parts={headline} /></h1>
                  <p className="sub">{current.sub}</p>
                  {current.cta ? (
                    <div className="hero-cta">
                      <button className="cta" type="button" onClick={() => startTrial(`hero_${scenario}`)}>
                        {current.cta} <span className="arr" aria-hidden="true">→</span>
                      </button>
                      <div className="cta-note"><span>7 дней бесплатно</span> · карта не нужна</div>
                    </div>
                  ) : null}
                </div>
                <div className="widget" key={scenario}>{renderWidget()}</div>
                <ul className="ticks">
                  {current.ticks.map((tick) => <li key={tick}>{tick}</li>)}
                </ul>
              </div>
            </div>
          </section>

          <section className="block">
            <div className="wrap">
              <h2>Вся работа селлера в одном окне</h2>
              <div className="jobs">
                {JOB_STAGES.map((stage, index) => {
                  const expanded = !phone || Boolean(openStages[index]);
                  return (
                    <div key={stage.title} className={`stage ${expanded ? "open" : ""}`}>
                      <h3>
                        <button
                          type="button"
                          className="stage-t"
                          disabled={!phone}
                          aria-expanded={expanded}
                          onClick={() => setOpenStages((state) => ({ ...state, [index]: !state[index] }))}
                        >
                          <span>{index + 1}</span>
                          {stage.title}
                          <i>{stage.jobs.length} {pluralTasks(stage.jobs.length)}</i>
                        </button>
                      </h3>
                      <div className="stage-jobs">
                        {stage.jobs.map((job) =>
                          job.s ? (
                            <button
                              key={job.b}
                              className={`job ${job.s === scenario ? "is-on" : ""}`}
                              type="button"
                              onClick={() => selectScenario(job.s, "jobs_map", { scroll: true })}
                            >
                              <b>{job.b}</b>
                              <span>{job.t}</span>
                            </button>
                          ) : (
                            <div key={job.b} className="job static">
                              <b>{job.b}</b>
                              <span>{job.t}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="block">
            <div className="wrap">
              <h2>Так выглядит сервис</h2>
              <div className="shots">
                <div className="seg shot-tabs" role="group" aria-label="Экран сервиса">
                  {SHOT_TABS.map(([key, label]) => (
                    <button key={key} type="button" aria-pressed={key === shot} onClick={() => setShot(key)}>{label}</button>
                  ))}
                </div>
                <figure className="shot">
                  <a href={shotSrc} target="_blank" rel="noopener noreferrer">
                    <img src={shotSrc} width="1280" height="720" loading="lazy" alt={activeShot[3]} />
                  </a>
                  <figcaption>{activeShot[2]}</figcaption>
                </figure>
                <p className="fine">Скриншоты на демо-данных.</p>
              </div>
            </div>
          </section>

          <section className="block">
            <div className="wrap">
              <h2>Как начать</h2>
              <ol className="steps">
                <li><b>Войдите по номеру телефона</b><span>Позвоните на номер, который мы покажем. Вход подтвердится сам, пароль не нужен.</span></li>
                <li>
                  <b>Подключите магазин</b>
                  <span>
                    Создайте ключ API в кабинете Wildberries или Ozon и вставьте его в сервис. Ключ проверяется обычно меньше минуты, дальше данные загружаются сами.{" "}
                    <a href="#faq" className="ulink" onClick={(event) => openFaq(event, "key_how")}>Какой нужен ключ</a>
                  </span>
                </li>
                <li><b>Загрузите себестоимость</b><span>Одним файлом Excel. После этого видно прибыль по каждому товару.</span></li>
                <li><b>Получите рекомендации</b><span>Когда закончится первая загрузка, AI-рекомендации разберут последнюю полную неделю: что сделать сегодня, что на этой неделе и за чем наблюдать.</span></li>
              </ol>
            </div>
          </section>

          <section className="block">
            <div className="wrap">
              <div className="price">
                <div>
                  <h2>7 дней бесплатно</h2>
                  <p>Отсчёт начнётся, когда вы подключите Wildberries или Ozon, а не с регистрации. Потом 1 290 ₽ в месяц. В подписку входят один кабинет Wildberries и один кабинет Ozon. Карта для пробного периода не нужна.</p>
                  <p className="terms">
                    Оплата раз в месяц. Автоплатёж включаете сами и отключаете в любой момент. Других платежей и комиссий нет.{" "}
                    <a className="ulink" href={legal.documents.offer} target="_blank" rel="noopener noreferrer">Условия в договоре оферты</a>
                  </p>
                </div>
                <button className="cta" type="button" onClick={() => startTrial("price")}>
                  Начать бесплатно <span className="arr" aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          </section>

          <section className="block" id="faq">
            <div className="wrap">
              <h2>Частые вопросы</h2>
              <div>
                {FAQ.map(([key, question, answers]) => (
                  <details
                    key={key}
                    data-q={key}
                    onToggle={(event) => {
                      if (event.currentTarget.open) trackMetrikaGoal(METRIKA_GOALS.landingFaqOpen, { question: key });
                    }}
                  >
                    <summary>{question}</summary>
                    {answers.map((answer) => <p key={answer.slice(0, 24)}>{answer}</p>)}
                  </details>
                ))}
              </div>
              <p className="faq-more">
                Не нашли ответ?{" "}
                <a href={legal.telegram} className="ulink" target="_blank" rel="noopener noreferrer" onClick={() => trackCallback("faq")}>
                  Напишите нам в Telegram
                </a>
              </p>
            </div>
          </section>
        </main>

        <footer className="foot">
          <div className="wrap fcols">
            <div className="fcol fabout">
              <Logo src={logo} />
              <p>Сервис для селлеров Wildberries и Ozon: рекомендации по товарам, юнит-экономика, P&amp;L, остатки и отзывы.</p>
            </div>
            <nav className="fcol" aria-label="Документы">
              <b>Документы</b>
              <a className="ulink" href={legal.documents.offer} target="_blank" rel="noopener noreferrer">Договор публичной оферты</a>
              <a className="ulink" href={legal.documents.privacy} target="_blank" rel="noopener noreferrer">Политика обработки персональных данных</a>
              <a className="ulink" href={legal.documents.consent} target="_blank" rel="noopener noreferrer">Согласие на обработку персональных данных</a>
            </nav>
            <div className="fcol">
              <b>Контакты и реквизиты</b>
              <span>{legal.owner}</span>
              <a className="ulink" href={`mailto:${legal.email}`}>{legal.email}</a>
              <a className="ulink" href={`tel:${legal.phone.replace(/[^\d+]/g, "")}`}>{legal.phone}</a>
              <a className="ulink" href={legal.documents.details} target="_blank" rel="noopener noreferrer">Полные реквизиты компании</a>
            </div>
            <div className="fcol">
              <b>Поддержка</b>
              <a className="ulink" href={legal.telegram} target="_blank" rel="noopener noreferrer" onClick={() => trackCallback("footer")}>
                Написать в Telegram
              </a>
            </div>
          </div>
          <div className="wrap fbottom">
            <span>© Упрости, {new Date().getFullYear()}</span>
            <span>Цены указаны в рублях, без скрытых платежей</span>
          </div>
        </footer>
      </div>

      <Modal
        open={authOpen}
        onCancel={() => setAuthOpen(false)}
        footer={null}
        centered
        width="min(460px, calc(100vw - 24px))"
        wrapClassName={`upl-theme upl-auth ${isDark ? "is-dark" : ""}`}
        // Заголовок только для экранных дикторов: rc-dialog ставит по нему aria-labelledby,
        // видимый заголовок рисует карточка входа.
        title={<span className="upl-sr-only">Вход в Упрости</span>}
        destroyOnClose={false}
        afterOpenChange={(open) => {
          // На сенсорных экранах не ставим фокус: клавиатура закрыла бы половину карточки.
          if (!open || !window.matchMedia?.("(hover: hover) and (pointer: fine)").matches) return;
          document.querySelector(".upl-auth .PhoneInputInput")?.focus?.({ preventScroll: true });
        }}
      >
        {savedCalc ? (
          <div className="saved">
            <SaveIcon />
            <span>
              Сохраним расчёт: {savedCalc.marketplace === "wb" ? "WB" : "Ozon"}
              {savedCalc.categoryName ? ` · ${savedCalc.categoryName}` : ""} · {money(savedCalc.result.price ?? savedCalc.input?.price)} →{" "}
              <b>{money(savedCalc.result.profit)}</b>
            </span>
          </div>
        ) : null}
        {renderAuthCard()}
      </Modal>
    </ConfigProvider>
  );
}
